/**
 * GoalDetailPage
 * Individual goal details with progress tracking
 * BP-06-03: Care Team & Goals Frontend
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  CheckCircle,
  XCircle,
  TrendingUp,
  Calendar,
  User,
  Target,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import {
  useGoalDetailPage,
  useDeleteGoal,
  useAchieveGoal,
  useDiscontinueGoal,
  useCreateProgress,
  useDeleteProgress,
} from '@/features/goals/hooks/useGoals';
import {
  GoalProgressChart,
  ProgressEntryForm,
  ProgressTimeline,
} from '../components';
import { GOAL_TYPE_LABELS, GOAL_STATUS_LABELS, PRIORITY_LABELS } from '@/features/goals/types/goalsTypes';
import type { ProgressEntryFormData } from '@/features/goals/types/goalsTypes';

export function GoalDetailPage() {
  const { childId, goalId } = useParams<{ childId: string; goalId: string }>();
  const navigate = useNavigate();
  const [showProgressForm, setShowProgressForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { goal, progress, isLoading, error } = useGoalDetailPage(goalId!);
  const deleteMutation = useDeleteGoal();
  const achieveMutation = useAchieveGoal();
  const discontinueMutation = useDiscontinueGoal();
  const createProgressMutation = useCreateProgress();
  const deleteProgressMutation = useDeleteProgress();

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(goalId!);
    navigate(`/children/${childId}/goals`);
  };

  const handleAchieve = async () => {
    await achieveMutation.mutateAsync(goalId!);
  };

  const handleDiscontinue = async () => {
    await discontinueMutation.mutateAsync(goalId!);
  };

  const handleAddProgress = async (data: ProgressEntryFormData) => {
    await createProgressMutation.mutateAsync({ goalId: goalId!, data });
    setShowProgressForm(false);
  };

  const handleDeleteProgress = async (entryId: string) => {
    await deleteProgressMutation.mutateAsync({ goalId: goalId!, entryId });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  if (error || !goal) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-terracotta" />
        <h2 className="text-xl font-semibold text-navy mb-2">Goal Not Found</h2>
        <p className="text-warm-gray mb-4">Unable to load goal details.</p>
        <button
          onClick={() => navigate(`/children/${childId}/goals`)}
          className="text-sage hover:text-sage-dark"
        >
          ← Back to Goals
        </button>
      </div>
    );
  }

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      not_started: 'bg-gray-100 text-gray-600',
      in_progress: 'bg-sage/10 text-sage-dark',
      achieved: 'bg-green-100 text-green-700',
      discontinued: 'bg-terracotta/10 text-terracotta',
    };
    return colors[status] || colors.not_started;
  };

  const getPriorityColor = (priority: string): string => {
    const colors: Record<string, string> = {
      high: 'text-red-500',
      medium: 'text-yellow-500',
      low: 'text-gray-400',
    };
    return colors[priority] || colors.medium;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/children/${childId}/goals`)}
        className="inline-flex items-center gap-2 text-warm-gray hover:text-navy mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Goals
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-2xl border border-warm-gray/10 overflow-hidden mb-6">
        <div className="p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
                  {GOAL_STATUS_LABELS[goal.status]}
                </span>
                {goal.isIepGoal && (
                  <span className="px-2.5 py-1 bg-navy/10 text-navy rounded-full text-xs font-medium">
                    IEP Goal
                  </span>
                )}
                <span className={getPriorityColor(goal.priority)}>
                  {PRIORITY_LABELS[goal.priority]} Priority
                </span>
              </div>

              <h1 className="text-2xl font-display font-semibold text-navy mb-2">{goal.title}</h1>

              {goal.description && (
                <p className="text-navy-light">{goal.description}</p>
              )}

              <div className="flex flex-wrap gap-4 mt-4 text-sm text-warm-gray">
                <span className="flex items-center gap-1.5">
                  <Target className="w-4 h-4" />
                  {GOAL_TYPE_LABELS[goal.goalType]}
                </span>
                {goal.category && (
                  <span>{goal.category}</span>
                )}
                {goal.targetDate && (
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    Target: {new Date(goal.targetDate).toLocaleDateString()}
                  </span>
                )}
                {goal.careTeamMember && (
                  <span className="flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    {goal.careTeamMember.name}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {goal.status !== 'achieved' && goal.status !== 'discontinued' && (
                <>
                  <button
                    onClick={handleAchieve}
                    disabled={achieveMutation.isPending}
                    className="inline-flex items-center gap-2 px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Mark as Achieved"
                  >
                    <CheckCircle className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleDiscontinue}
                    disabled={discontinueMutation.isPending}
                    className="inline-flex items-center gap-2 px-3 py-2 text-terracotta hover:bg-terracotta/10 rounded-lg transition-colors"
                    title="Discontinue Goal"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </>
              )}
              <button
                onClick={() => navigate(`/children/${childId}/goals/${goalId}/edit`)}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-sage text-sage hover:bg-sage/10 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 text-warm-gray hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="border-t border-warm-gray/10 px-6 sm:px-8 py-4 bg-cream/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-navy">Progress</span>
            <span className="text-sm text-warm-gray">
              {goal.currentValue || goal.baselineValue || '0'} / {goal.targetValue || '100'}
            </span>
          </div>
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                goal.progressPercentage >= 100 ? 'bg-green-500' :
                goal.progressPercentage >= 50 ? 'bg-sage' : 'bg-terracotta'
              }`}
              style={{ width: `${Math.min(100, goal.progressPercentage)}%` }}
            />
          </div>
          <p className="text-sm text-warm-gray mt-1">{goal.progressPercentage}% complete</p>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Progress Chart */}
        <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display font-semibold text-navy">Progress Over Time</h2>
          </div>
          <GoalProgressChart
            entries={progress?.items || []}
            measurementType={goal.measurementType}
            targetValue={goal.targetValue}
            isLoading={!progress}
          />
        </div>

        {/* Progress Timeline */}
        <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display font-semibold text-navy">Progress History</h2>
            <button
              onClick={() => setShowProgressForm(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-sage/10 text-sage-dark hover:bg-sage/20 rounded-lg text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Log Progress
            </button>
          </div>
          <ProgressTimeline
            entries={progress?.items || []}
            measurementType={goal.measurementType}
            onDeleteEntry={handleDeleteProgress}
            isLoading={!progress}
          />
        </div>
      </div>

      {/* Additional Details */}
      {(goal.notes || goal.iepArea) && (
        <div className="mt-6 bg-white rounded-xl border border-warm-gray/10 p-6">
          <h2 className="text-lg font-display font-semibold text-navy mb-4">Additional Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {goal.iepArea && (
              <div>
                <dt className="text-sm text-warm-gray mb-1">IEP Area</dt>
                <dd className="text-navy font-medium">{goal.iepArea}</dd>
              </div>
            )}
            {goal.reviewFrequency && (
              <div>
                <dt className="text-sm text-warm-gray mb-1">Review Frequency</dt>
                <dd className="text-navy font-medium capitalize">{goal.reviewFrequency}</dd>
              </div>
            )}
            {goal.notes && (
              <div className="md:col-span-2">
                <dt className="text-sm text-warm-gray mb-1">Notes</dt>
                <dd className="text-navy-light">{goal.notes}</dd>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress Form Modal */}
      {showProgressForm && (
        <div className="fixed inset-0 bg-navy/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-lifted w-full max-w-md p-6">
            <ProgressEntryForm
              goalId={goalId!}
              measurementType={goal.measurementType}
              targetValue={goal.targetValue}
              onSubmit={handleAddProgress}
              onCancel={() => setShowProgressForm(false)}
              isSubmitting={createProgressMutation.isPending}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-navy/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-lifted w-full max-w-md p-6">
            <h2 className="text-xl font-display font-semibold text-navy mb-2">Delete Goal?</h2>
            <p className="text-warm-gray mb-6">
              Are you sure you want to delete "{goal.title}"? All progress history will be permanently removed.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete Goal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GoalDetailPage;


